import { useState, useEffect, useCallback } from 'react';
import { apiService } from '../services/api';
import { AudioTrack } from '../types';

interface UseAudioTrackReturn {
  track: AudioTrack | null;
  loading: boolean;
  error: string | null;
  fetchTrack: (duration: number) => Promise<void>;
}

export const useAudioTrack = (
  trackId: string | undefined,
  defaultDuration: number,
  autoFetch: boolean = true
): UseAudioTrackReturn => {
  const [duration, setDuration] = useState<number>(defaultDuration);
  const [track, setTrack] = useState<AudioTrack | null>(null);
  const [loading, setLoading] = useState(autoFetch);
  const [error, setError] = useState<string | null>(null);

  const fetchTrack = useCallback(
    async (duration: number) => {
      if (!trackId) {
        setError('No track ID provided');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        const response = await apiService.getAudioTrack(trackId, duration);

        if (!response) {
          setError('Track not found');
          setTrack(null);
          return;
        }

        setTrack(response);
      } catch (err) {
        console.error('Error fetching audio track:', err);
        setError('Error loading audio track');
        setTrack(null);
      } finally {
        setLoading(false);
      }
    },
    [trackId]
  );

  useEffect(() => {
    if (autoFetch) {
      fetchTrack(defaultDuration);
    }
  }, [fetchTrack, autoFetch, defaultDuration]);

  return {
    track,
    loading,
    error,
    fetchTrack,
  };
};
